import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@jeraisonne/ui';
import { Heart, X } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { supabase } from '@/lib/supabase';
import { useRevokeParentLink } from '@/lib/parent-link';
import { filterByQuery, hasSearchQuery } from '@/lib/contact-search';

interface Props {
  studentId: string;
  /** Recherche de la page « Mes contacts » : ne montre que les parents correspondants. */
  filter?: string;
}

type ParentRow = {
  id: string;
  status: string;
  parent: { id: string; display_name: string; avatar_url: string | null } | null;
};

export function useMyParents(studentId: string) {
  return useQuery({
    queryKey: ['my-parents', studentId],
    enabled: !!studentId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parent_links')
        .select(
          `id, status, parent:profiles!parent_links_parent_id_fkey ( id, display_name:visible_name, avatar_url )`,
        )
        .eq('student_id', studentId)
        .eq('status', 'active');
      if (error) throw error;
      return (data ?? []) as unknown as ParentRow[];
    },
  });
}

export function ParentsSection({ studentId, filter = '' }: Props) {
  const { data: parents = [], isLoading } = useMyParents(studentId);
  const revoke = useRevokeParentLink();
  const shown = filterByQuery(parents, filter, (row) => [row.parent?.display_name]);

  // On n'affiche pas la section vide pour éviter le bruit côté élève
  // (la grande majorité n'a pas de parent lié actif).
  if (!isLoading && parents.length === 0) return null;
  // Pendant une recherche, une liste sans résultat disparaît.
  if (!isLoading && hasSearchQuery(filter) && shown.length === 0) return null;

  return (
    <section className="space-y-3">
      <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
        Mes parents ({shown.length})
      </p>
      {isLoading ? (
        <Card className="border-ink-100 animate-pulse">
          <CardContent className="flex items-center gap-3 py-3">
            <div className="bg-ink-100 h-10 w-10 rounded-full" />
            <div className="bg-ink-100 h-4 w-32 rounded" />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {shown.map((row) => (
            <Card key={row.id} className="border-ink-100">
              <CardContent className="flex items-center gap-3 py-3">
                <AvatarCircle
                  name={row.parent?.display_name ?? 'Parent'}
                  src={row.parent?.avatar_url ?? null}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate text-sm font-semibold">
                    {row.parent?.display_name ?? 'Parent'}
                  </p>
                  <p className="flex items-center gap-1 text-xs text-pink-500">
                    <Heart className="h-3 w-3" />
                    Suit ta progression
                  </p>
                </div>
                {row.parent?.id && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Retirer ${row.parent?.display_name ?? 'ce parent'} ?`)) {
                        revoke.mutate(row.parent!.id);
                      }
                    }}
                    disabled={revoke.isPending}
                    className="text-ink-400 -my-2 -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:text-red-500"
                    aria-label={`Retirer ${row.parent?.display_name ?? 'ce parent'}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
